// src/components/NavBar.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PairingSelector from './PairingSelector';

/**
 * Navigationsleiste
 * - Dunkle Leiste (#222222), links immer: "Kontakt"
 * - Rechts nur sichtbar, wenn:
 *      1) der User eingeloggt ist UND
 *      2) wir uns NICHT auf einer Public-Seite befinden
 *   (So bleibt nach Logout/Reload wirklich nur "Kontakt" sichtbar.)
 */
export default function NavBar({ user, onLogoutClick, onSupportClick }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Lokaler Fallback-User, falls das Prop beim Initial-Render noch leer ist
  const [selfUser, setSelfUser] = useState(null);

  // HashRouter-Fix: Bei hash-basierten URLs steckt der echte Pfad im location.hash
  const getEffectivePath = (loc) =>
    (loc.pathname === '/' && loc.hash)
      ? loc.hash.slice(1).split('?')[0] // "#/hr/overview?lang=de" -> "/hr/overview"
      : loc.pathname;

  // WhoAmI-Fallback: nur ausführen, wenn das user-Prop keine Rolle enthält
  useEffect(() => {
    let cancelled = false;

    async function ensureUser() {
      if (user && user.role) return; // Prop reicht, kein Fallback nötig

      try {
        const r = await fetch('/mentoring/whoami', { credentials: 'include' });
        if (!r.ok) return;

        const data = await r.json();
        const u = data?.user || (data?.ok ? data : null);
        if (!cancelled && u && (u.role || u.email)) {
          setSelfUser(u);
        }
      } catch {
        // still und leise – Nav bleibt dann im Public-Mode
      }
    }

    ensureUser();
    return () => { cancelled = true; };
  }, [user]);

  // ✅ FIX: user nur dann bevorzugen, wenn er wirklich eine Rolle enthält.
  // Sonst selfUser verwenden (damit Reload nicht "ausgeloggt" aussieht).
  const propHasRole = !!(user && user.role);
  const selfHasRole = !!(selfUser && selfUser.role);

  const currentUser = propHasRole ? user : (selfUser || user);
  const resolvedRole = (propHasRole ? user?.role : selfUser?.role) || user?.role || selfUser?.role;
  const isLoggedIn = propHasRole || selfHasRole;

  // 🔎 Mini-Debug: Was bekommt die NavBar beim Rendern?
  useEffect(() => {
    console.log('NavBar render', {
      path: location.pathname,
      hash: location.hash,
      effectivePath: getEffectivePath(location),
      hasUserProp: !!user,
      hasUserPropRole: propHasRole,
      hasSelfUser: !!selfUser,
      hasSelfUserRole: selfHasRole,
      role: resolvedRole,
      isLoggedIn,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, user, selfUser, isLoggedIn]);

  // Seiten, die als "öffentlich" gelten – hier niemals KI/Logout anzeigen
  const PUBLIC_PATHS = new Set([
    '/', '/login', '/nl-login', '/register', '/forgot-password', '/reset-password',
    '/kontakt', '/contact'
  ]);

  const effectivePath = getEffectivePath(location);

  // Auth-Routen sollen ohne NavBar laufen
  const HIDE_NAV_ON = new Set([
    '/login',
    '/nl-login',
    '/register',
    '/forgot-password',
    '/reset-password',
  ]);

  const cleanPath = (effectivePath || '').split('?')[0];

  if (HIDE_NAV_ON.has(cleanPath)) {
    return null;
  }

  const onPublicPage = PUBLIC_PATHS.has(effectivePath);

  // KI-Button nur für HR-Rollen; NL/RL haben eigenen Button im MPI-Dashboard
  const showKiButton = ['hr', 'hr_admin'].includes(resolvedRole);

  // Styles
  const navStyle = {
    background: '#222222',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 20,
  };

  const leftBox = { display: 'flex', alignItems: 'center', gap: 12 };
  const rightBox = { display: 'flex', alignItems: 'center', gap: 12 };

  const linkBtn = {
    background: 'none',
    border: 'none',
    color: '#e5e7eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'none',
  };

  const pillBtn = {
    background: '#9AF68C',
    color: '#0b0f0a',
    border: 'none',
    borderRadius: 999,
    padding: '8px 14px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <nav style={navStyle}>
      {/* Links: Kontakt – immer sichtbar */}
      <div style={leftBox}>
        <Link to="/kontakt" style={linkBtn}>
          {t('contact', 'Kontakt')}
        </Link>
      </div>

      {/* Rechts: nur wenn eingeloggt UND nicht auf einer Public-Seite */}
      {!onPublicPage && isLoggedIn && (
        <div style={rightBox}>
          <PairingSelector user={currentUser} />
          {showKiButton && (
            <button
              type="button"
              style={pillBtn}
              onClick={() => { if (typeof onSupportClick === 'function') onSupportClick(); }}
            >
              {resolvedRole === 'mentee'
                ? t('kiMentor', 'KI-Mentor')
                : t('kiAssistant', 'KI-Assistent')}
            </button>
          )}

          <button
            type="button"
            style={linkBtn}
            onClick={() => {
              if (typeof onLogoutClick === 'function') onLogoutClick();
              else navigate('/logout');
            }}
          >
            {t('logoutButton', 'Abmelden')}
          </button>
        </div>
      )}
    </nav>
  );
}
